/**
 * mockResponses — バックエンド不達時のデモレスポンスデータ
 *
 * superagent-sre バックエンドが起動していない場合に、
 * リアルな AI 応答をシミュレートしてチャット体験を提供する。
 *
 * ux-psychology:
 *   Progressive Disclosure — 段階的にメッセージ + 成果物を返す
 *   Peak-End Rule — 最終的にアクション可能な成果物で完結
 *
 * effective-typescript:
 *   Record<FunctionType, T> で全機能タイプを網羅保証
 */

import type {
  FunctionType,
  ChatMessage,
  ChatArtifact,
  SreTask,
  SreExpert,
} from '../types';

// ── デモ応答データ（機能別）──────────────────────────────

interface MockScenario {
  /** PMO によるコンテキスト説明メッセージ */
  readonly pmoMessage: string;
  /** エージェントによる分析結果メッセージ */
  readonly agentMessages: readonly {
    readonly agentRole: string;
    readonly content: string;
  }[];
  /** 成果物 */
  readonly artifacts: readonly {
    readonly type: 'text' | 'markdown';
    readonly name: string;
    readonly content: string;
  }[];
  /** フォローアップサジェスチョン */
  readonly suggestions: readonly string[];
}

export const MOCK_SCENARIOS: Record<FunctionType, MockScenario> = {
  security_alert: {
    pmoMessage:
      'セキュリティ分析チームが対応を開始しました。Security Agent と Log Agent が連携して調査しています。',
    agentMessages: [
      {
        agentRole: 'Security Agent',
        content: `## セキュリティアラート分析結果

### 検出事項
本番環境（ap-northeast-1）で以下のセキュリティアラートを検出しました:

| # | アラート | 重要度 | リソース |
|---|---------|--------|---------|
| 1 | 不審なAPIコール検出 | **HIGH** | \`arn:aws:iam::123456789:role/prod-api-role\` |
| 2 | S3バケットのパブリックアクセス設定変更 | **CRITICAL** | \`s3://prod-data-lake\` |
| 3 | Security Groupの許可ルール追加（0.0.0.0/0） | **HIGH** | \`sg-0abc123def456\` |

### 即座の推奨アクション
1. **S3バケットのパブリックアクセスをブロック** — Block Public Access 設定を有効化
2. **Security Group の 0.0.0.0/0 ルールを削除** — 最小権限の原則に従い、必要なCIDRのみ許可
3. **IAMロールのアクティビティログを確認** — CloudTrail で過去24時間の操作を監査

### 影響範囲
- 影響サービス: API Gateway → Lambda → DynamoDB パイプライン
- 推定影響ユーザー: 約 12,000 DAU
- データ漏洩リスク: **中〜高**（S3バケットにPIIデータあり）`,
      },
      {
        agentRole: 'Log Agent',
        content: `## ログ相関分析

CloudTrail / VPC Flow Logs / GuardDuty の横断分析結果:

- **09:12:34 UTC** — \`AssumeRole\` 成功（ソースIP: 203.0.113.42 — 社外IP）
- **09:12:45 UTC** — \`s3:PutBucketAcl\` 実行 → パブリックアクセス許可に変更
- **09:13:01 UTC** — \`ec2:AuthorizeSecurityGroupIngress\` → 0.0.0.0/0 追加
- **09:13-09:25** — 異常なデータ転送量検出（通常の **8.3倍**）

> ⚠️ 一連のアクションは同一セッションから実行されており、**不正アクセスの可能性が高い**と判断します。`,
      },
    ],
    artifacts: [
      {
        type: 'markdown',
        name: 'セキュリティ分析レポート',
        content:
          '# セキュリティインシデント分析\n\n## エグゼクティブサマリー\n本番環境で不審なAPIアクティビティを検出。S3パブリックアクセス変更とSG開放が同一セッションで実行され、不正アクセスの疑い。\n\n## 重要度: CRITICAL\n## 推奨対応: 即座のアクセス遮断 + フォレンジック調査',
      },
      {
        type: 'markdown',
        name: '推奨アクション一覧',
        content:
          '# 推奨アクション\n\n1. [ ] S3 Block Public Access 有効化\n2. [ ] Security Group 0.0.0.0/0 ルール削除\n3. [ ] IAM ロールの一時停止\n4. [ ] CloudTrail ログの保全\n5. [ ] インシデント対応チーム招集',
      },
    ],
    suggestions: [
      'S3バケットの自動修復Runbookを実行しますか？',
      'GuardDuty の過去30日間の検出履歴を表示してください',
      'このインシデントのタイムラインを作成してください',
    ],
  },

  cost_analysis: {
    pmoMessage:
      'コスト分析チームが対応を開始しました。Cost Agent と Performance Agent が連携して分析しています。',
    agentMessages: [
      {
        agentRole: 'Cost Agent',
        content: `## コスト分析結果

### 月次コスト概要（2026年2月）

| サービス | 今月 | 前月 | 増減 |
|---------|------|------|------|
| EC2 | ¥2,340,000 | ¥1,890,000 | **+23.8%** 🔴 |
| RDS | ¥890,000 | ¥870,000 | +2.3% |
| S3 | ¥320,000 | ¥290,000 | +10.3% |
| Lambda | ¥180,000 | ¥165,000 | +9.1% |
| CloudFront | ¥420,000 | ¥400,000 | +5.0% |
| **合計** | **¥4,150,000** | **¥3,615,000** | **+14.8%** |

### コスト異常検出
- **EC2 (ap-northeast-1)**: \`m5.4xlarge\` × 12台が常時稼働 → 使用率 **平均 18%**
- **RDS Multi-AZ**: 開発環境で Multi-AZ が有効（不要）
- **未使用EIP**: 5個の Elastic IP が未アタッチ（¥5,400/月）

### 推定削減額: **¥980,000/月**（約23.6%削減可能）`,
      },
      {
        agentRole: 'Performance Agent',
        content: `## パフォーマンス観点からのコスト最適化提案

### ライトサイジング分析
EC2 インスタンスの CPU/Memory 使用率を過去30日間で分析:

- \`m5.4xlarge\` → \`m5.xlarge\` に変更可能（8台）: **¥640,000/月削減**
- \`r5.2xlarge\` → \`r5.xlarge\` に変更可能（2台）: **¥120,000/月削減**

### Savings Plans / Reserved Instances
- 1年 Compute Savings Plan で **さらに 30% 削減可能**
- 推定年間節約額: **¥4,200,000**

> 💡 パフォーマンスへの影響なく、大幅なコスト削減が実現できます。`,
      },
    ],
    artifacts: [
      {
        type: 'markdown',
        name: 'コスト最適化レポート',
        content:
          '# コスト最適化レポート\n\n## 推定年間削減額: ¥11,760,000\n\n### 優先度別アクション\n1. EC2 ライトサイジング: ¥640,000/月\n2. RDS 開発環境 Single-AZ化: ¥120,000/月\n3. Savings Plan 導入: ¥350,000/月\n4. 未使用リソース削除: ¥5,400/月',
      },
    ],
    suggestions: [
      'EC2ライトサイジングのRunbookを実行しますか？',
      'Savings Planのシミュレーションを表示してください',
      '部門別のコスト配分を分析してください',
    ],
  },

  performance_diagnosis: {
    pmoMessage:
      'パフォーマンス診断チームが対応を開始しました。Performance Agent と Log Agent が連携して調査しています。',
    agentMessages: [
      {
        agentRole: 'Performance Agent',
        content: `## パフォーマンス診断結果

### 現在のシステム状態

| メトリクス | 現在値 | 閾値 | ステータス |
|-----------|--------|------|----------|
| API レイテンシ (p99) | **2,340ms** | 500ms | 🔴 Critical |
| CPU 使用率 (avg) | **87%** | 70% | 🟠 Warning |
| メモリ使用率 | 62% | 80% | 🟢 Normal |
| DB接続プール使用率 | **94%** | 80% | 🔴 Critical |
| エラーレート (5xx) | **3.2%** | 1% | 🔴 Critical |

### 根本原因分析
1. **DB接続プール枯渇** — RDS のmax_connections到達により新規クエリがキュー待ち
2. **スロークエリ** — \`orders\` テーブルの全件スキャン（インデックス欠落）
3. **CPU スパイク** — Lambda の同時実行が上限に到達、スロットリング発生

### 推奨対応
1. RDS接続プール上限を引き上げ（100 → 200）
2. \`orders.customer_id\` にインデックス追加
3. Lambda Provisioned Concurrency 設定（100 → 300）`,
      },
    ],
    artifacts: [
      {
        type: 'markdown',
        name: 'パフォーマンス診断レポート',
        content:
          '# パフォーマンス診断\n\n## 重要度: CRITICAL\n## 影響: API レイテンシ 4.7倍悪化\n\n### 即座のアクション\n1. [ ] DB接続プール拡大\n2. [ ] スロークエリのインデックス追加\n3. [ ] Lambda 同時実行数引き上げ',
      },
    ],
    suggestions: [
      'DB接続プール拡大のRunbookを実行しますか？',
      '過去7日間のレイテンシトレンドを表示してください',
      'スロークエリの詳細分析を行いますか？',
    ],
  },

  iam_audit: {
    pmoMessage:
      'IAM 監査チームが対応を開始しました。IAM Agent と Security Agent が連携して監査しています。',
    agentMessages: [
      {
        agentRole: 'IAM Agent',
        content: `## IAM 監査結果

### 権限監査サマリー

| カテゴリ | 検出数 | 重要度 |
|---------|--------|--------|
| 過剰権限ロール | **8** | 🔴 HIGH |
| 未使用IAMユーザー | **12** | 🟠 MEDIUM |
| MFA未設定ユーザー | **3** | 🔴 HIGH |
| アクセスキー未ローテーション(>90日) | **15** | 🟠 MEDIUM |
| ポリシー違反(AdministratorAccess) | **2** | 🔴 CRITICAL |

### 重要な検出事項

#### 1. AdministratorAccess ポリシーの不正使用
- \`dev-team-role\` — 開発チームに \`AdministratorAccess\` が付与されています
- \`ci-cd-pipeline-role\` — CI/CDパイプラインに過剰な権限

#### 2. 未使用 IAM ユーザー（90日以上ログインなし）
\`\`\`
user-tanaka-old, user-suzuki-temp, service-legacy-api,
batch-job-user-v1, test-admin-user, ...他7名
\`\`\`

#### 3. MFA 未設定の特権ユーザー
- \`admin-yamada\` — フル管理者権限（MFA なし ⚠️）
- \`ops-sato\` — 運用管理権限（MFA なし ⚠️）
- \`security-admin\` — セキュリティ管理者（MFA なし ⚠️）

### 推奨アクション
1. **即座**: MFA 未設定の特権ユーザーに MFA を強制
2. **今週中**: AdministratorAccess を最小権限ポリシーに置換
3. **今月中**: 未使用ユーザー/キーの棚卸し・削除`,
      },
      {
        agentRole: 'Security Agent',
        content: `## セキュリティ観点からの IAM リスク評価

### リスクスコア: **78/100**（高リスク）

主要リスク要因:
- **特権エスカレーション**: dev-team-role から本番リソースへのアクセスが可能
- **水平移動リスク**: 未使用アカウントの侵害→他リソースへのアクセス
- **コンプライアンス違反**: SOC2 / ISO27001 の最小権限要件を満たしていない

> 🛡️ AWS IAM Access Analyzer のレコメンデーションに基づく自動修復が可能です。`,
      },
    ],
    artifacts: [
      {
        type: 'markdown',
        name: 'IAM 監査レポート',
        content:
          '# IAM 監査レポート\n\n## リスクスコア: 78/100 (HIGH)\n## 検出事項: 40件\n## 即座対応必要: 5件\n\n### コンプライアンス状態\n- SOC2: ❌ 非準拠（3項目）\n- ISO27001: ⚠️ 要改善（2項目）\n- CIS Benchmark: 68% 準拠',
      },
      {
        type: 'markdown',
        name: '改善アクション一覧',
        content:
          '# IAM 改善アクション\n\n## 即座（今日中）\n1. [ ] MFA強制: admin-yamada, ops-sato, security-admin\n\n## 短期（今週中）\n2. [ ] AdministratorAccess → カスタムポリシーへ置換\n3. [ ] 未使用アクセスキーの無効化\n\n## 中期（今月中）\n4. [ ] 未使用ユーザーの削除\n5. [ ] IAM Access Analyzer 有効化\n6. [ ] SCPによるガードレール設定',
      },
    ],
    suggestions: [
      'MFA強制ポリシーの適用Runbookを実行しますか？',
      '過剰権限ロールの詳細分析を表示してください',
      'IAM Access Analyzer の推奨事項を確認しますか？',
    ],
  },

  log_analysis: {
    pmoMessage:
      'ログ分析チームが対応を開始しました。Log Agent と Security Agent が連携して分析しています。',
    agentMessages: [
      {
        agentRole: 'Log Agent',
        content: `## ログ分析結果

### 分析対象
- **期間**: 過去24時間（2026-02-23 00:00 〜 2026-02-24 00:00 UTC）
- **ソース**: CloudWatch Logs, VPC Flow Logs, ALB Access Logs, CloudTrail

### 異常検出サマリー

| ログソース | 総イベント数 | 異常検出数 | 重要度 |
|-----------|------------|----------|--------|
| ALB Access Logs | 2,340,000 | **847** | 🔴 HIGH |
| CloudTrail | 156,000 | **23** | 🟠 MEDIUM |
| VPC Flow Logs | 8,900,000 | **1,204** | 🟠 MEDIUM |
| Application Logs | 890,000 | **156** | 🟢 LOW |

### 主要な異常パターン

#### 1. 5xx エラーの急増（ALB）
- **09:00-09:30**: 5xx レート **12.3%**（通常 0.2%）
- 影響エンドポイント: \`/api/v1/orders\`, \`/api/v1/payments\`
- 根本原因: バックエンドの DB 接続タイムアウト

#### 2. 不審なアウトバウンド通信（VPC Flow Logs）
- 宛先: \`45.33.32.156\`（脅威インテリジェンスDB該当）
- 通信量: **2.3 GB**（過去24時間）
- ソース: \`i-0abc123def456\`（prod-api-server-03）

#### 3. 未承認 API コール（CloudTrail）
- \`DescribeInstances\` × 1,200回/時（通常の15倍）
- ソース: \`arn:aws:iam::123456789:user/monitoring-user\``,
      },
    ],
    artifacts: [
      {
        type: 'markdown',
        name: 'ログ分析レポート',
        content:
          '# ログ異常分析レポート\n\n## 検出異常: 2,230件\n## 重要度: HIGH\n\n### 推奨アクション\n1. [ ] DB接続プール調査\n2. [ ] 不審IPへのアウトバウンド通信遮断\n3. [ ] monitoring-user の権限見直し',
      },
    ],
    suggestions: [
      '不審なアウトバウンド通信の詳細を表示してください',
      'エラーレートの時系列グラフを生成してください',
      'CloudTrail の異常パターンを深掘りしますか？',
    ],
  },

  integrated_report: {
    pmoMessage:
      '統合レポート生成チームが対応を開始しました。全6ドメインのエージェントが連携して包括的な分析を実施しています。',
    agentMessages: [
      {
        agentRole: 'Report Agent',
        content: `## 統合 SRE レポート（2026年2月 月次）

### エグゼクティブサマリー

運用全体の健全性スコア: **72/100**（前月比 -5pt）

| ドメイン | スコア | トレンド | 主要課題 |
|---------|--------|---------|---------|
| セキュリティ | 65/100 | 📉 | S3パブリックアクセス、MFA未設定 |
| コスト | 78/100 | 📉 | EC2過剰プロビジョニング (+24%) |
| パフォーマンス | 70/100 | 📉 | API レイテンシ悪化 (p99: 2.3s) |
| IAM | 58/100 | ➡️ | 過剰権限 8ロール、未使用12ユーザー |
| ログ | 82/100 | 📈 | 異常検出精度向上 |
| SLO | 75/100 | 📉 | 可用性 SLO: 99.85%（目標 99.95%） |

### 今月のインシデント
- **INC-2026-0201**: API レイテンシ劣化（解決済み、MTTR: 45分）
- **INC-2026-0215**: S3 パブリックアクセス検出（対応中）
- **INC-2026-0220**: Lambda スロットリング（解決済み、MTTR: 20分）

### 改善提案（優先度順）
1. 🔴 IAM 権限の棚卸し — リスクスコア 78/100 の早期改善
2. 🔴 S3 セキュリティ設定の自動修復 Runbook 導入
3. 🟠 EC2 ライトサイジング — 年間 ¥11.7M 削減可能
4. 🟠 SLO エラーバジェット回復計画の策定
5. 🟢 ログ異常検出の閾値チューニング`,
      },
    ],
    artifacts: [
      {
        type: 'markdown',
        name: '月次 SRE レポート',
        content:
          '# 月次 SRE レポート — 2026年2月\n\n## 総合スコア: 72/100\n## インシデント: 3件（MTTR平均: 33分）\n## コスト: ¥4.15M（予算比 +15%）\n## SLO達成率: 4/6 (67%)\n\n### 次月の重点施策\n1. IAM 監査・是正\n2. コスト最適化実行\n3. SLO エラーバジェット回復',
      },
      {
        type: 'markdown',
        name: 'アクションアイテム一覧',
        content:
          '# アクションアイテム\n\n## Critical\n- [ ] IAM 過剰権限是正（担当: セキュリティチーム）\n- [ ] S3 自動修復 Runbook（担当: SRE チーム）\n\n## High\n- [ ] EC2 ライトサイジング（担当: インフラチーム）\n- [ ] SLO 回復計画策定（担当: SRE リード）\n\n## Medium\n- [ ] ログ閾値チューニング（担当: 監視チーム）',
      },
    ],
    suggestions: [
      '各ドメインの詳細スコアを表示してください',
      'インシデントのトレンド分析を行いますか？',
      'コスト削減の ROI シミュレーションを実行しますか？',
    ],
  },
};

// ── デモ タスクデータ ──────────────────────────────────

export const MOCK_TASKS: Record<FunctionType, readonly SreTask[]> = {
  security_alert: [
    { id: 'sec-1', title: 'アラート分析', description: 'セキュリティアラートの重要度判定と影響範囲分析', agentId: 'security-agent', inputStatus: 'ok' },
    { id: 'sec-2', title: '脅威インテリジェンス照合', description: '外部脅威DBとの照合・相関分析', agentId: 'security-agent', inputStatus: 'ok' },
    { id: 'sec-3', title: '修復アクション提案', description: '自動修復Runbookの選定と実行計画', agentId: 'security-agent', inputStatus: 'partial' },
  ],
  cost_analysis: [
    { id: 'cost-1', title: 'リソース使用率分析', description: 'EC2/RDS/Lambdaの使用率分析とライトサイジング提案', agentId: 'cost-agent', inputStatus: 'ok' },
    { id: 'cost-2', title: '予算超過アラート', description: '予算との差分分析と異常コスト検出', agentId: 'cost-agent', inputStatus: 'ok' },
    { id: 'cost-3', title: 'Savings Plan分析', description: 'RI/SPの最適化シミュレーション', agentId: 'cost-agent', inputStatus: 'missing' },
  ],
  performance_diagnosis: [
    { id: 'perf-1', title: 'メトリクス診断', description: 'CPU/Memory/Latency/ErrorRateの閾値分析', agentId: 'perf-agent', inputStatus: 'ok' },
    { id: 'perf-2', title: 'ボトルネック特定', description: 'スロークエリ・接続プール・スロットリングの根本原因分析', agentId: 'perf-agent', inputStatus: 'ok' },
    { id: 'perf-3', title: 'キャパシティプランニング', description: '将来の負荷予測とスケーリング計画', agentId: 'perf-agent', inputStatus: 'partial' },
  ],
  iam_audit: [
    { id: 'iam-1', title: '権限棚卸し', description: 'IAMロール/ユーザーの権限分析と過剰権限検出', agentId: 'iam-agent', inputStatus: 'ok' },
    { id: 'iam-2', title: 'コンプライアンスチェック', description: 'SOC2/ISO27001/CIS Benchmark準拠確認', agentId: 'iam-agent', inputStatus: 'ok' },
    { id: 'iam-3', title: 'MFA/キーローテーション監査', description: 'MFA未設定・アクセスキー未ローテーションの検出', agentId: 'iam-agent', inputStatus: 'ok' },
  ],
  log_analysis: [
    { id: 'log-1', title: '異常パターン検出', description: 'CloudWatch/VPC Flow/ALBログの異常パターン分析', agentId: 'log-agent', inputStatus: 'ok' },
    { id: 'log-2', title: '相関分析', description: '複数ログソースの横断相関分析', agentId: 'log-agent', inputStatus: 'ok' },
    { id: 'log-3', title: 'トレンド分析', description: 'エラーレート・アクセスパターンの時系列分析', agentId: 'log-agent', inputStatus: 'partial' },
  ],
  integrated_report: [
    { id: 'report-1', title: '月次レポート生成', description: '全ドメイン横断の統合レポート自動生成', agentId: 'report-agent', inputStatus: 'ok' },
    { id: 'report-2', title: 'トレンド分析', description: '過去3ヶ月のスコア推移とインシデント傾向', agentId: 'report-agent', inputStatus: 'ok' },
    { id: 'report-3', title: 'アクションアイテム整理', description: '優先度別の改善提案とアサイン', agentId: 'report-agent', inputStatus: 'ok' },
  ],
};

// ── デモ エキスパートデータ ──────────────────────────────

export const MOCK_EXPERTS: Record<FunctionType, readonly SreExpert[]> = {
  security_alert: [
    { id: 'exp-sec-1', role: 'Security Analyst', icon: 'security', description: 'セキュリティアラートの重要度判定とトリアージ', enabled: true, category: 'recommended' },
    { id: 'exp-sec-2', role: 'Threat Intelligence', icon: 'shield', description: '脅威インテリジェンスDBとの照合', enabled: true, category: 'recommended' },
    { id: 'exp-sec-3', role: 'Incident Responder', icon: 'warning', description: 'インシデント対応と修復アクション', enabled: false, category: 'other' },
  ],
  cost_analysis: [
    { id: 'exp-cost-1', role: 'Cost Optimizer', icon: 'money', description: 'コスト最適化戦略の立案', enabled: true, category: 'recommended' },
    { id: 'exp-cost-2', role: 'FinOps Analyst', icon: 'chart', description: '予算管理と異常コスト検出', enabled: true, category: 'recommended' },
    { id: 'exp-cost-3', role: 'Cloud Architect', icon: 'cloud', description: 'アーキテクチャ観点からのコスト最適化', enabled: false, category: 'other' },
  ],
  performance_diagnosis: [
    { id: 'exp-perf-1', role: 'Performance Engineer', icon: 'speed', description: 'パフォーマンスメトリクスの分析と診断', enabled: true, category: 'recommended' },
    { id: 'exp-perf-2', role: 'Database Expert', icon: 'database', description: 'DB性能チューニングとクエリ最適化', enabled: true, category: 'recommended' },
    { id: 'exp-perf-3', role: 'SRE Lead', icon: 'person', description: 'SLO/SLI設計とキャパシティプランニング', enabled: false, category: 'other' },
  ],
  iam_audit: [
    { id: 'exp-iam-1', role: 'IAM Specialist', icon: 'key', description: 'IAM権限の分析と最小権限設計', enabled: true, category: 'recommended' },
    { id: 'exp-iam-2', role: 'Compliance Officer', icon: 'gavel', description: 'SOC2/ISO27001準拠チェック', enabled: true, category: 'recommended' },
    { id: 'exp-iam-3', role: 'Security Architect', icon: 'architecture', description: 'ゼロトラストアーキテクチャ設計', enabled: false, category: 'other' },
  ],
  log_analysis: [
    { id: 'exp-log-1', role: 'Log Analyst', icon: 'search', description: 'ログパターン分析と異常検出', enabled: true, category: 'recommended' },
    { id: 'exp-log-2', role: 'SIEM Specialist', icon: 'monitor', description: 'SIEM連携と相関分析', enabled: true, category: 'recommended' },
    { id: 'exp-log-3', role: 'Forensic Analyst', icon: 'fingerprint', description: 'デジタルフォレンジック調査', enabled: false, category: 'other' },
  ],
  integrated_report: [
    { id: 'exp-report-1', role: 'Report Analyst', icon: 'assessment', description: '統合レポート生成と分析', enabled: true, category: 'recommended' },
    { id: 'exp-report-2', role: 'Data Scientist', icon: 'analytics', description: 'トレンド分析と予測モデリング', enabled: true, category: 'recommended' },
    { id: 'exp-report-3', role: 'Operations Manager', icon: 'business', description: '運用改善提案と優先度判定', enabled: false, category: 'other' },
  ],
};
